import Header from '@/components/header/Header.vue';
import Footer from '@/components/footer/Footer.vue';
import BookCard from '@/components/book-card/BookCard.vue';
import axios from '@/config/axios';

import { saveTrendingToCache, loadTrendingFromCache, clearTrendingCache } from '@/utils/trendingCache';
import { saveRecommendToCache, loadRecommendFromCache, clearRecommendCache } from '@/utils/recommendCache';
function processTrendingCovers(books) {
  return books.map(book => ({
    ...book,
    cover: book.cover && !(book.cover.startsWith('data:') || book.cover.startsWith('http'))
      ? 'data:image/png;base64,' + book.cover
      : book.cover
  }));
}

export default {
  name: 'Home',
  components: {
    Header,
    Footer,
    BookCard
  },
  data() {
    return {
      searchKey: '',
      searchedKey: '',
      books: [],
      currentPage: 0, // 0-indexed for request
      totalPages: 0,
      totalElements: 0,
      isLoading: false,
      hasSearched: false,
      errorMessage: null,

      debounceTimer: null,
      showSuggestions: false,
      recentSearches: [],
      highlightedIndex: -1,
      showHomeSections: true,
      showRecommendations: true,
      trendingBooks: [],
      allTrendingBooks: [],
      recommendedBooks: [],
      isTrendingLoading: false,
      isRecommendLoading: false,
      isRefreshingRecommend: false,
      suggestedBooks: [],
      suggestTimer: null,

      // Drag to scroll properties
      isDown: false,
      startX: 0,
      scrollLeft: 0,
      hasDragged: false,

      // Responsive flag
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      isMobile: typeof window !== 'undefined' ? window.innerWidth < 576 : false
    };
  },
  watch: {
    '$route': {
      handler(toRoute) {
        const path = toRoute.path || '/';
        const isTrendingRoute = (path === '/trending') || (toRoute.query && toRoute.query.type === 'trending');

        let queryKey = '';
        let queryPage = 0;

        if (isTrendingRoute) {
          queryKey = '';
          queryPage = toRoute.query?.page ? parseInt(toRoute.query.page, 10) : 0;
        } else if (path === '/search') {
          if (toRoute.query && (toRoute.query.key !== undefined || toRoute.query.keyword !== undefined)) {
            this.$router.replace('/').catch(() => {});
            return;
          }
          queryKey = (history.state && history.state.key !== undefined)
            ? history.state.key
            : (sessionStorage.getItem('lastSearchKey') || window.currentSearchKey || '');
          const rawPage = (history.state && history.state.page !== undefined)
            ? history.state.page
            : (sessionStorage.getItem('lastSearchPage') || 0);
          queryPage = Math.min(9, Math.max(0, parseInt(rawPage, 10) || 0));

          if (!queryKey) {
            this.$router.replace('/').catch(() => {});
            return;
          }
        } else {
          queryKey = toRoute.query?.key || '';
          queryPage = toRoute.query?.page ? parseInt(toRoute.query.page, 10) : 0;
        }

        const isSearchRoute = (path === '/search') || isTrendingRoute || !!queryKey;

        if (this.searchKey !== queryKey) {
          this.searchKey = queryKey;
        }
        window.currentSearchKey = queryKey;
        if (queryKey) {
          sessionStorage.setItem('lastSearchKey', queryKey);
          sessionStorage.setItem('lastSearchPage', String(queryPage));
        }

        this.showHomeSections = !isSearchRoute && (path === '/');
        if (this.showHomeSections && this.trendingBooks.length === 0) {
          this.fetchTrendingBooks();
        }
        this.fetchBooks(queryPage);
      },
      deep: true,
      immediate: true
    },
    searchKey(newVal) {
      this.getSuggestions(newVal);
      if (window.currentSearchKey !== newVal) {
        window.currentSearchKey = newVal;
        const event = new CustomEvent('search-key-sync', { detail: newVal });
        window.dispatchEvent(event);
      }
    }
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('token');
    },
    filteredRecentSearches() {
      if (!this.searchKey) return this.recentSearches;
      const query = this.searchKey.toLowerCase();
      return this.recentSearches.filter(item =>
        item.toLowerCase().includes(query)
      );
    },
    filteredBooks() {
      return this.suggestedBooks;
    },
    flatSelectableItems() {
      const list = [];
      // Recent searches
      this.filteredRecentSearches.forEach(item => {
        list.push({ type: 'recent', value: item });
      });
      // Matching books from local database (only when searchKey is not empty)
      if (this.searchKey && this.filteredBooks && this.filteredBooks.length > 0) {
        this.filteredBooks.slice(0, 3).forEach(book => {
          list.push({ type: 'book', value: book.name, book: book });
        });
      }
      return list;
    },
    // Custom visible pages logic to handle ellipses (...) nicely for large catalogs
    visiblePages() {
      const current = this.currentPage;
      const total = this.totalPages;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i);
      }

      const pages = [];
      // Always show page 1 (which is index 0)
      pages.push(0);

      const start = Math.max(1, current - 1);
      const end = Math.min(total - 2, current + 1);

      if (start > 1) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < total - 2) {
        pages.push('...');
      }

      // Always show last page
      pages.push(total - 1);

      return pages;
    },
    /**
     * Trending books:
     * - iPhone / Mobile (< 576px): 6 cuốn (2 hàng x 3 cột)
     * - iPad / Tablet (576px - 1199px): 4 cuốn (1 hàng x 4 cột)
     * - Desktop (>= 1200px): 5 cuốn (1 hàng x 5 cột)
     */
    trendingBooksDisplay() {
      if (this.windowWidth < 576) {
        return this.trendingBooks.slice(0, 6);
      }
      if (this.windowWidth < 1200) {
        return this.trendingBooks.slice(0, 4);
      }
      return this.trendingBooks.slice(0, 5);
    },
    /**
     * Recommended books:
     * - iPhone (< 576px): 9 cuốn (3 hàng x 3 cột)
     * - iPad / Tablet (576px - 1199px): 8 cuốn (2 hàng x 4 cột)
     * - Desktop (>= 1200px): 10 cuốn (2 hàng x 5 cột)
     */
    recommendedBooksDisplay() {
      if (this.windowWidth < 576) {
        return this.recommendedBooks.slice(0, 9);
      }
      if (this.windowWidth < 1200) {
        return this.recommendedBooks.slice(0, 8);
      }
      return this.recommendedBooks.slice(0, 10);
    },
    /**
     * Skeleton count cho Recommendations:
     * - iPhone (< 576px): 9 khung (3 hàng x 3 cột)
     * - iPad / Tablet (576px - 1199px): 8 khung (2 hàng x 4 cột)
     * - Desktop (>= 1200px): 10 khung (2 hàng x 5 cột)
     */
    recommendSkeletonCount() {
      if (this.windowWidth < 576) return 9;
      if (this.windowWidth < 1200) return 8;
      return 10;
    },
    /**
     * Skeleton count cho Trending Books:
     * - iPhone (< 576px): 6 khung (2 hàng x 3 cột)
     * - iPad / Tablet (576px - 1199px): 4 khung (1 hàng x 4 cột)
     * - Desktop (>= 1200px): 5 khung (1 hàng x 5 cột)
     */
    trendingSkeletonCount() {
      if (this.windowWidth < 576) return 6;
      if (this.windowWidth < 1200) return 4;
      return 5;
    }
  },
  mounted() {
    this.loadRecentSearches();
    if (this.showHomeSections) {
      this.showRecommendations = !!localStorage.getItem('token');
      if (this.showRecommendations) {
        this.fetchRecommendedBooks();
      }
    }
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('preferences-updated', this.handlePreferencesUpdated);
    window.addEventListener('trending-cache-updated-silently', this.handleTrendingCacheUpdatedSilently);
    // Lắng nghe sự kiện đăng nhập mới để xóa cache cũ và gọi lại API đề xuất
    window.addEventListener('user-logged-in', this.handleUserLoggedIn);
    window.addEventListener('user-logged-out', this.handleUserLoggedOut);
    window.addEventListener('search-triggered', this.handleSearchTriggered);
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('preferences-updated', this.handlePreferencesUpdated);
    window.removeEventListener('trending-cache-updated-silently', this.handleTrendingCacheUpdatedSilently);
    window.removeEventListener('user-logged-in', this.handleUserLoggedIn);
    window.removeEventListener('user-logged-out', this.handleUserLoggedOut);
    window.removeEventListener('search-triggered', this.handleSearchTriggered);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      if (typeof window !== 'undefined') {
        this.windowWidth = window.innerWidth;
        this.isMobile = window.innerWidth < 576;
      }
    },
    /**
     * Trả về userId hiện tại để làm key cache.
     * Dùng username hoặc sub từ JWT nếu có, fallback về 'default'.
     */
    getCurrentUserId() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return 'default';
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || payload.username || payload.userId || 'default';
      } catch {
        return 'default';
      }
    },

    /**
     * Được gọi khi nhận event 'user-logged-in':
     * Xóa cache đề xuất cũ (của session trước) và gọi API mới.
     */
    async handleUserLoggedIn() {
      const userId = this.getCurrentUserId();
      await clearRecommendCache(userId);
      this.showRecommendations = true;
      this.fetchRecommendedBooks(true);
    },

    /**
     * Được gọi khi nhận event 'preferences-updated':
     * Xóa cache đề xuất cũ và gọi lại AI đề xuất mới.
     */
    async handlePreferencesUpdated() {
      const userId = this.getCurrentUserId();
      await clearRecommendCache(userId);
      this.fetchRecommendedBooks(true);
    },

    /**
     * Được gọi khi nhận event 'user-logged-out':
     * An toàn xóa state và ẩn section đề xuất.
     */
    async handleUserLoggedOut() {
      try {
        const userId = this.getCurrentUserId();
        await clearRecommendCache(userId);
      } catch (e) {
        console.error('Error clearing recommend cache on logout:', e);
      }
      this.showRecommendations = false;
      this.recommendedBooks = [];
    },
    isHotBook(book) {
      if (!book) return false;
      const path = (this.$route && this.$route.path) ? this.$route.path : '';
      const queryType = (this.$route && this.$route.query) ? (this.$route.query.type || '') : '';
      const isTrendingRoute = path.includes('trending') || queryType === 'trending';
      const score = parseFloat(book.evaluate);
      const isHighRating = !isNaN(score) && score >= 4.5;
      return isTrendingRoute || isHighRating;
    },
    getReadingStatusLabel(book) {
      if (!book || book.progressStatus === undefined || book.progressStatus === null) return null;
      const status = Number(book.progressStatus);
      switch (status) {
        case 1: return 'Muốn đọc';
        case 2: return 'Đang đọc';
        case 3: return 'Đã đọc xong';
        case 4: return 'Tạm ngưng';
        default: return null;
      }
    },
    getStatusBadgeClass(status) {
      const s = Number(status);
      switch (s) {
        case 1: return "badge-status-want";
        case 2: return "badge-status-reading";
        case 3: return "badge-status-completed";
        case 4: return "badge-status-paused";
        default: return "badge-status-unread";
      }
    },
    handleTrendingCacheUpdatedSilently() {
      // Xóa cache trên RAM để bắt buộc fetchTrendingBooks phải đọc lại từ IndexedDB
      this.allTrendingBooks = [];
      this.trendingBooks = [];
      if (this.showHomeSections) {
        this.fetchTrendingBooks();
      }
    },
    async fetchTrendingBooks() {
      if (!this.showHomeSections || this.isTrendingLoading || (this.trendingBooks && this.trendingBooks.length > 0)) {
        return;
      }
      // Try in-memory first
      if (this.allTrendingBooks && this.allTrendingBooks.length > 0) {
        this.trendingBooks = this.allTrendingBooks.slice(0, 6);
        return;
      }
      // Try IndexedDB cache
      const cached = await loadTrendingFromCache();
      if (cached && cached.length > 0) {
        this.allTrendingBooks = processTrendingCovers(cached);
        this.trendingBooks = this.allTrendingBooks.slice(0, 6);
        return;
      }
      // Fetch from API
      this.isTrendingLoading = true;
      axios.get('book/v1/trending')
        .then(response => {
          if (response.data.status === '200' && response.data.result) {
            const all = Array.isArray(response.data.result) ? response.data.result : [];
            saveTrendingToCache(all);
            this.allTrendingBooks = processTrendingCovers(all);
            this.trendingBooks = this.allTrendingBooks.slice(0, 6);
          }
        })
        .catch(err => console.error('Trending fetch error:', err))
        .finally(() => { this.isTrendingLoading = false; });
    },
    /**
     * Tải sách đề xuất:
     * - Mặc định: load từ IndexedDB cache (nếu có và đủ 10 cuốn), không gọi API.
     * - forceRefresh = true: xóa cache → gọi API → lưu cache mới.
     */
    async fetchRecommendedBooks(forceRefresh = false) {
      if (!this.showRecommendations || !localStorage.getItem('token')) return;
      const userId = this.getCurrentUserId();

      // Nếu không force refresh, chỉ dùng cache nếu đã có đủ tối thiểu 10 cuốn đề xuất
      if (!forceRefresh) {
        const cached = await loadRecommendFromCache(userId);
        if (cached && cached.length >= 10) {
          this.recommendedBooks = processTrendingCovers(cached);
          this.isRecommendLoading = false;
          return;
        }
      }

      // Gọi API (lần đầu login, khi force refresh, hoặc khi cache cũ chưa đủ 10 cuốn)
      this.recommendedBooks = [];
      this.isRecommendLoading = true;
      axios.get('book/get-suggest', { _skip403Redirect: true })
        .then(async response => {
          if (response.data.status === '200' && response.data.result) {
            const all = Array.isArray(response.data.result) ? response.data.result : [];
            this.recommendedBooks = processTrendingCovers(all);
            // Lưu vào IndexedDB
            await saveRecommendToCache(all, userId);
          }
        })
        .catch(err => console.error('Recommendations fetch error:', err))
        .finally(() => { this.isRecommendLoading = false; });
    },

    /**
     * Làm mới sách đề xuất: xóa cache → gọi API → lưu cache mới.
     * Được gọi khi người dùng bấm nút "Làm mới đề xuất".
     */
    async refreshRecommendedBooks() {
      if (this.isRefreshingRecommend || this.isRecommendLoading) return;
      this.isRefreshingRecommend = true;
      const userId = this.getCurrentUserId();
      await clearRecommendCache(userId);
      await this.fetchRecommendedBooks(true);
      this.isRefreshingRecommend = false;
    },

    viewAllBooks() {
      this.$router.push('/trending').catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err);
        }
      });
    },
    handleKeyDown(e) {
      if (!this.showSuggestions) {
        if (e.key === 'ArrowDown') {
          this.showSuggestions = true;
          this.highlightedIndex = -1;
          e.preventDefault();
        }
        return;
      }

      const items = this.flatSelectableItems;
      if (items.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.highlightedIndex = (this.highlightedIndex + 1) % items.length;
        this.scrollToHighlighted();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.highlightedIndex = (this.highlightedIndex - 1 + items.length) % items.length;
        this.scrollToHighlighted();
      } else if (e.key === 'Enter') {
        if (this.highlightedIndex >= 0 && this.highlightedIndex < items.length) {
          e.preventDefault();
          this.selectSuggestion(items[this.highlightedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.closeSuggestions();
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const activeEl = this.$el.querySelector('.search-suggestions-dropdown .suggestion-item-highlighted');
        if (activeEl) {
          activeEl.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    handleDocumentClick(e) {
      const form = this.$el.querySelector('.search-form');
      if (form && !form.contains(e.target)) {
        this.closeSuggestions();
      }
    },
    handleInputFocus() {
      this.showSuggestions = true;
      this.highlightedIndex = -1;
    },
    closeSuggestions() {
      this.showSuggestions = false;
      this.highlightedIndex = -1;
    },
    loadRecentSearches() {
      try {
        const stored = localStorage.getItem('recentSearches');
        this.recentSearches = stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error loading recent searches:', e);
        this.recentSearches = [];
      }
    },
    saveSearchKey(key) {
      if (!key || !key.trim()) return;
      const cleanKey = key.trim();
      const filtered = this.recentSearches.filter(item => item.toLowerCase() !== cleanKey.toLowerCase());
      filtered.unshift(cleanKey);
      this.recentSearches = filtered.slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
    },
    removeRecentSearch(val) {
      this.recentSearches = this.recentSearches.filter(item => item !== val);
      localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
    },
    clearAllRecent() {
      this.recentSearches = [];
      localStorage.removeItem('recentSearches');
    },
    selectSuggestion(item) {
      if (!item) return;
      this.searchKey = item.value;
      this.triggerSearch();
    },
    triggerSearch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      const key = (this.searchKey || '').trim();
      if (key) {
        this.saveSearchKey(key);
      }
      window.currentSearchKey = key;
      sessionStorage.setItem('lastSearchKey', key);
      sessionStorage.setItem('lastSearchPage', '0');
      const event = new CustomEvent('search-key-sync', { detail: key });
      window.dispatchEvent(event);

      if (this.$route.path === '/search') {
        window.history.replaceState({ ...window.history.state, key, page: 0 }, '');
        this.fetchBooks(0);
      } else {
        this.$router.push({
          path: '/search',
          state: { key, page: 0 }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      }
      this.closeSuggestions();
    },

    clearInput() {
      this.searchKey = '';
      window.currentSearchKey = '';
      sessionStorage.removeItem('lastSearchKey');
      sessionStorage.removeItem('lastSearchPage');
      const event = new CustomEvent('search-key-sync', { detail: '' });
      window.dispatchEvent(event);
      if (this.$route && this.$route.path === '/search') {
        this.$router.push('/').catch(() => {});
      }
    },
    async fetchBooks(page = 0) {
      const path = this.$route.path || '';
      const isTrending = path === '/trending' || (this.$route.query && this.$route.query.type === 'trending');
      const searchKeyVal = isTrending
        ? ''
        : ((history.state && history.state.key !== undefined)
            ? history.state.key
            : (this.searchKey || this.$route.query?.key || window.currentSearchKey || sessionStorage.getItem('lastSearchKey') || ''));

      // If we are not searching and not showing trending list, do not load search results
      if (!searchKeyVal && !isTrending && path !== '/search') {
        this.books = [];
        this.totalPages = 0;
        this.totalElements = 0;
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.errorMessage = null;

      if (isTrending) {
        // Try in-memory first
        if (this.allTrendingBooks && this.allTrendingBooks.length > 0) {
          const pageSize = 10;
          const startIndex = page * pageSize;
          this.books = this.allTrendingBooks.slice(startIndex, startIndex + pageSize);
          this.currentPage = page;
          this.totalPages = Math.ceil(this.allTrendingBooks.length / pageSize);
          this.totalElements = this.allTrendingBooks.length;
          this.searchedKey = '';
          this.hasSearched = true;
          this.isLoading = false;
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        // Try IndexedDB cache
        const cached = await loadTrendingFromCache();
        if (cached && cached.length > 0) {
          this.allTrendingBooks = processTrendingCovers(cached);
          const pageSize = 10;
          const startIndex = page * pageSize;
          this.books = this.allTrendingBooks.slice(startIndex, startIndex + pageSize);
          this.currentPage = page;
          this.totalPages = Math.ceil(this.allTrendingBooks.length / pageSize);
          this.totalElements = this.allTrendingBooks.length;
          this.searchedKey = '';
          this.hasSearched = true;
          this.isLoading = false;
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        // Fetch from API
        axios.get('book/v1/trending')
          .then(response => {
            const data = response.data;
            if (data.status === '200') {
              if (data.result) {
                const all = Array.isArray(data.result) ? data.result : [];
                saveTrendingToCache(all);
                this.allTrendingBooks = processTrendingCovers(all);
                const pageSize = 10;
                const startIndex = page * pageSize;
                this.books = this.allTrendingBooks.slice(startIndex, startIndex + pageSize);
                this.currentPage = page;
                this.totalPages = Math.ceil(this.allTrendingBooks.length / pageSize);
                this.totalElements = this.allTrendingBooks.length;
              } else {
                this.allTrendingBooks = [];
                this.books = [];
                this.totalPages = 0;
                this.totalElements = 0;
              }
              this.searchedKey = '';
              this.hasSearched = true;
            } else if (data.status === '404') {
              this.allTrendingBooks = [];
              this.books = [];
              this.totalPages = 0;
              this.totalElements = 0;
              this.searchedKey = '';
              this.hasSearched = true;
            } else {
              this.errorMessage = data.message || 'Đã có lỗi xảy ra.';
            }
            this.isLoading = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          })
          .catch(error => {
            console.error('Top view page error:', error);
            this.errorMessage = error.response?.data?.message || 'Không thể kết nối đến máy chủ. Vui lòng kiểm tra lại kết nối mạng.';
            this.isLoading = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
      } else {
        const url = 'book/v1/search';
        const safePage = Math.min(9, Math.max(0, page));
        const params = {
          key: searchKeyVal,
          page: safePage
        };

        axios.get(url, { params })
          .then(response => {
            const data = response.data;
            if (data.status === '200') {
              if (data.result) {
                const content = data.result.content || [];
                this.books = content.map(book => ({
                  ...book,
                  cover: book.cover && !(book.cover.startsWith('data:') || book.cover.startsWith('http'))
                    ? 'data:image/png;base64,' + book.cover
                    : book.cover
                }));
                this.currentPage = safePage;
                this.totalPages = Math.min(10, data.result.totalPages || 0);
                this.totalElements = data.result.totalElements || 0;
              } else {
                this.books = [];
                this.totalPages = 0;
                this.totalElements = 0;
              }
              this.searchedKey = searchKeyVal;
              this.hasSearched = true;
            } else if (data.status === '404') {
              this.books = [];
              this.totalPages = 0;
              this.totalElements = 0;
              this.searchedKey = searchKeyVal;
              this.hasSearched = true;
            } else if (data.status === '500') {
              this.errorMessage = data.message || 'Lỗi hệ thống, vui lòng kiểm tra kết nối hoặc liên hệ quản trị viên.';
            } else {
              this.errorMessage = data.message || 'Đã có lỗi xảy ra.';
            }
            this.isLoading = false;
            this.scrollToResults();
          })
          .catch(error => {
            console.error('Book search error:', error);
            this.errorMessage = error.response?.data?.message || 'Không thể kết nối đến máy chủ. Vui lòng kiểm tra lại kết nối mạng.';
            this.isLoading = false;
            this.scrollToResults();
          });
      }
    },
    getSuggestions(query) {
      if (!query || !query.trim()) {
        this.suggestedBooks = [];
        return;
      }
      if (this.suggestTimer) clearTimeout(this.suggestTimer);
      this.suggestTimer = setTimeout(() => {
        axios.get('book/v1/search', {
          params: {
            key: query.trim(),
            page: 0,
            size: 3
          }
        })
          .then(response => {
            const data = response.data;
            if (data.status === '200' && data.result) {
              const content = data.result.content || [];
              this.suggestedBooks = content.map(book => ({
                ...book,
                cover: book.cover && !(book.cover.startsWith('data:') || book.cover.startsWith('http'))
                  ? 'data:image/png;base64,' + book.cover
                  : book.cover
              }));
            } else {
              this.suggestedBooks = [];
            }
          })
          .catch(() => {
            this.suggestedBooks = [];
          });
      }, 800);
    },
    scrollToResults() {
      this.$nextTick(() => {
        // Close virtual keyboard on mobile devices
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
        const doScroll = () => {
          const element = document.querySelector('.results-display-section');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        };
        // Small delay to wait for keyboard collapse animation & layout reflow
        setTimeout(doScroll, 250);
        // Second attempt after images load & layout settles (fixes offset on hard reload)
        setTimeout(doScroll, 900);
      });
    },
    goToPage(page) {
      if (page === '...' || page < 0 || page >= this.totalPages) {
        return;
      }
      const isTrending = this.$route.path === '/trending' || this.$route.query?.type === 'trending';
      if (isTrending) {
        this.$router.push({
          path: '/trending',
          query: { ...this.$route.query, page }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      } else {
        sessionStorage.setItem('lastSearchPage', String(page));
        window.history.replaceState({ ...window.history.state, key: this.searchKey, page }, '');
        this.fetchBooks(page);
      }
    },
    handleSearchTriggered(e) {
      const { key, page } = e.detail || {};
      if (key !== undefined) {
        this.searchKey = key;
      }
      this.fetchBooks(page || 0);
    },
    retrySearch() {
      this.fetchBooks(this.currentPage);
    },
    getAuthorsString(book) {
      if (!book.authors || book.authors.length === 0) return 'Khuyết danh';
      return book.authors.map(a => a.name).join(', ');
    },
    getTranslatorsString(book) {
      if (!book.translators || book.translators.length === 0) return '';
      return book.translators.map(t => t.name).join(', ');
    },
    getCategoryList(book) {
      if (!book) return [];
      let list = [];
      if (Array.isArray(book.categories) && book.categories.length > 0) {
        list = book.categories.map(c => typeof c === 'object' ? (c.name || c.categoryName || c.title) : c).filter(Boolean);
      } else if (Array.isArray(book.genres) && book.genres.length > 0) {
        list = book.genres.map(g => typeof g === 'object' ? (g.name || g.genreName || g.title) : g).filter(Boolean);
      } else if (book.category) {
        const catName = typeof book.category === 'string' ? book.category : (book.category.name || book.category.categoryName);
        if (catName) list = [catName];
      } else if (book.categoryName) {
        list = [book.categoryName];
      }
      return list.slice(0, 3);
    },
    getCategoriesString(book) {
      return this.getCategoryList(book).join(', ');
    },
    handleCoverError(book) {
      // Set cover to null dynamically to trigger fallback book visual cover
      book.cover = null;
    },

    getFallbackCoverClass(book) {
      const idStr = String(book.id || book.name);
      let hash = 0;
      for (let i = 0; i < idStr.length; i++) {
        hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash % 4) + 1;
      return `fallback-cover-variant-${index}`;
    },
    startDrag(e) {
      const container = e.currentTarget;
      this.isDown = true;
      this.hasDragged = false;
      container.classList.add('active-dragging');
      this.startX = e.pageX - container.offsetLeft;
      this.scrollLeft = container.scrollLeft;
    },
    stopDrag(e) {
      this.isDown = false;
      const container = e.currentTarget;
      if (container) {
        container.classList.remove('active-dragging');
      }
    },
    onDrag(e) {
      if (!this.isDown) return;
      e.preventDefault();
      const container = e.currentTarget;
      const x = e.pageX - container.offsetLeft;
      const walk = (x - this.startX) * 1.5;
      if (Math.abs(walk) > 5) {
        this.hasDragged = true;
      }
      container.scrollLeft = this.scrollLeft - walk;
    },
    handleCardClick(bookId) {
      if (this.hasDragged) {
        this.hasDragged = false;
        return;
      }
      this.$router.push('/book/' + bookId);
    }
  }
};